import { generatePath } from 'react-router-dom';

import {
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { AttributeType } from 'api/attributes/requests';
import { useCompleteOnboardingStep } from 'api/company/hooks';
import { useInvalidateFavorites } from 'api/favorites/hooks';
import { waitForElasticRefresh } from 'api/hooks';
import { useInvalidateJourneyActionForms } from 'api/journey-action-forms/hooks';
import { handleError } from 'api/query-client';
import { JourneyAction, JourneyType } from 'api/types';
import { routePaths } from 'ui/authenticated/navigation';

import {
  addJourneyTemplate,
  cancelJourneyChanges,
  createAttribute,
  createJourney,
  deleteJourney,
  duplicateJourney,
  DuplicateJourneyData,
  editJourney,
  JourneyAttributeData,
  JourneySettings,
  JourneyUserSettings,
  loadActionEmailTemplate,
  loadJourney,
  loadJourneyAttributes,
  loadJourneyIssues,
  loadJourneyOverview,
  loadJourneys,
  loadJourneyUserSettings,
  loadJourneyV2,
  moveJourney,
  PaginatedJourneysResponse,
  publishJourney,
  removeAttribute,
  updateAttribute,
  updateAttributeOrders,
  updateJourneyCollapse,
  updateJourneySettings,
  updateJourneyUserSettings,
} from './requests';

const getJourneysQueryKey = (queryString?: string) => ['journeys', queryString].filter(Boolean);

export const useJourneys = (queryString: string, disabled?: boolean) => {
  const query = useQuery<PaginatedJourneysResponse | JourneyType[], AxiosError, JourneyType[]>({
    queryKey: getJourneysQueryKey(queryString),
    queryFn: () => loadJourneys(queryString),
    keepPreviousData: true,
    enabled: !disabled,
    select: (data) => {
      return Array.isArray(data) ? data : data?.results ?? [];
    },
  });
  return query;
};

export const useCreateJourney = (
  options?: UseMutationOptions<{ shortId: string }, any, { title: string; workspace: string }>
) => {
  const { invalidateJourneys } = useInvalidateJourneys();
  return useMutation({
    ...options,
    mutationFn: (data) => createJourney(data),
    onSuccess: (...data) => {
      invalidateJourneys();
      options?.onSuccess?.(...data);
    },
  });
};

export const getJourneyQueryKey = (journeyId?: string) => ['journey', journeyId].filter(Boolean);
const getJourneyAttributesQueryKey = (
  journeyId?: string,
  params?: Record<string, string | number | boolean>
) => ['journey-attributes', journeyId, params].filter(Boolean);

const getJourneyIssuesQueryKey = (journeyId?: string) =>
  ['journey-issues', journeyId].filter(Boolean);

export const useJourneyCached = (journeyId: JourneyType['shortId']) => {
  const queryKey = getJourneyQueryKey(journeyId);
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData(queryKey) as ReturnType<typeof useJourney>['data'];
  return data;
};

export const useUpdateJourneyUserCollapse = (journeyId: JourneyType['shortId']) => {
  const queryClient = useQueryClient();
  const updateMutation = useUpdateUserCollapse(journeyId);

  const update = (collapsedActions: Iterable<JourneyAction['shortId']>) => {
    const queryKey = getJourneyQueryKey(journeyId);
    const newActions = [...collapsedActions];
    const currentQueryData = queryClient.getQueryData(queryKey) as JourneyType;
    const newJourneyData = {
      ...currentQueryData,
      collapsedActions: newActions,
    };
    queryClient.setQueryData(queryKey, newJourneyData);
    updateMutation.mutate(newActions);
  };

  return update;
};

export const useJourney = (
  journeyId: JourneyType['shortId'] = '',
  options?: { onSuccess?: (data: JourneyType) => void; onError?: () => void }
) => {
  return useQuery({
    queryKey: getJourneyQueryKey(journeyId),
    queryFn: () => loadJourney(journeyId),
    enabled: Boolean(journeyId),
    ...options,
  });
};

export const useAddJourneyTemplate = () => {
  return useMutation({
    mutationFn: addJourneyTemplate,
  });
};

export const useJourneyV2 = (
  journeyId: JourneyType['shortId'] = '',
  options?: { onSuccess?: (data: JourneyType) => void }
) => {
  return useQuery({
    queryKey: getJourneyQueryKey(journeyId),
    queryFn: () => loadJourneyV2(journeyId),
    enabled: Boolean(journeyId),
    ...options,
  });
};

export const useInvalidateJourneys = () => {
  const queryClient = useQueryClient();

  const invalidateJourneys = () => {
    return queryClient.invalidateQueries(getJourneysQueryKey());
  };
  const invalidateJourneysStats = () => {
    return queryClient.invalidateQueries(getJourneyStatsKey());
  };

  const invalidateJourney = (shortId?: JourneyType['shortId']) => {
    return Promise.all([
      queryClient.invalidateQueries(getJourneyQueryKey(shortId)),
      queryClient.invalidateQueries(getJourneyIssuesQueryKey(shortId)),
    ]);
  };

  const invalidateJourneyAttributes = (shortId?: JourneyType['shortId']) => {
    return queryClient.invalidateQueries(getJourneyAttributesQueryKey(shortId));
  };

  return {
    invalidateJourneys,
    invalidateJourney,
    invalidateJourneysStats,
    invalidateJourneyAttributes,
  };
};

type CreateAttributeProps = {
  journeyShortId: string;
  data: JourneyAttributeData;
};

export const useCreateAttribute = (
  options?: UseMutationOptions<AttributeType, AxiosError, CreateAttributeProps>
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney, invalidateJourneyAttributes } = useInvalidateJourneys();
  return useMutation({
    mutationFn: ({ journeyShortId, data }: CreateAttributeProps) =>
      createAttribute(journeyShortId, data),
    ...options,
    onSuccess: (data, variables, context) => {
      invalidateJourney(variables.journeyShortId);
      invalidateJourneyAttributes(variables.journeyShortId);
      options?.onSuccess?.(data, variables, context);
    },
    onError: (_, variables) => {
      handleError(t('attributeNotSaved'), () => invalidateJourney(variables.journeyShortId));
    },
  });
};

export const useUpdateAttribute = (journeyShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney } = useInvalidateJourneys();
  return useMutation({
    mutationFn: updateAttribute,
    onSuccess: () => {
      invalidateJourney(journeyShortId);
    },
    onError: () => {
      handleError(t('attributeNotSaved'), () => invalidateJourney(journeyShortId));
    },
  });
};

export const useRemoveAttribute = (journeyShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney } = useInvalidateJourneys();
  return useMutation({
    mutationFn: (shortId: string) => removeAttribute(shortId),
    onSuccess: () => {
      invalidateJourney(journeyShortId);
    },
    onError: () => {
      handleError(t('attributeNotSaved'), () => invalidateJourney(journeyShortId));
    },
  });
};

export const useUpdateAttributeOrders = (journeyShortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney } = useInvalidateJourneys();
  return useMutation({
    mutationFn: (data: Record<string, number>) => updateAttributeOrders(journeyShortId, data),
    onSuccess: () => {
      invalidateJourney(journeyShortId);
    },
    onError: () => {
      handleError(t('attributeOrdersNotSaved'), () => invalidateJourney(journeyShortId));
    },
  });
};

export const useEditJourney = (options?: { onSuccess?: (data: JourneyType) => void }) => {
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateFavorites } = useInvalidateFavorites();
  return useMutation(editJourney, {
    onSuccess: (data) => {
      invalidateJourney(data.shortId);
      invalidateFavorites();
      options?.onSuccess?.(data);
    },
  });
};

export const useMoveJourney = (shortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney, invalidateJourneys } = useInvalidateJourneys();
  return useMutation({
    mutationFn: (workspaceId: string) => moveJourney(shortId, workspaceId),
    onSuccess: () => {
      showSuccess(t('journeyMoved'));
      waitForElasticRefresh(() => {
        invalidateJourneys();
        invalidateJourney(shortId);
      });
    },
    onError: () => {
      handleError(t('journeyNotMoved'));
    },
  });
};

export const usePublishJourney = (shortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney } = useInvalidateJourneys();
  const completeOnboardingStep = useCompleteOnboardingStep();
  return useMutation({
    mutationFn: (data?: { changeTravellerJourneyIds?: string[] }) => publishJourney(shortId, data),
    onSuccess: () => {
      invalidateJourney(shortId);
      completeOnboardingStep('publishJourney');
    },
    onError: () => {
      handleError(t('journeyNotPublished'));
    },
  });
};

export const useActionEmailTemplate = (action: 'journey' | 'traveller', shortId: string) => {
  return useQuery<string>({
    queryKey: [`${action}-email-template`, shortId],
    queryFn: () => loadActionEmailTemplate(action, shortId),
  });
};

export const useCancelJourneyChanges = (options?: UseMutationOptions<unknown, any, any>) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourney } = useInvalidateJourneys();
  const { invalidateJourneyActionForms, invalidateJourneyActionFormsList } =
    useInvalidateJourneyActionForms();
  return useMutation({
    ...options,
    mutationFn: (shortId) => cancelJourneyChanges(shortId),
    onSuccess: (...data) => {
      invalidateJourney(data[1]);
      invalidateJourneyActionForms();
      invalidateJourneyActionFormsList();
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('journeyChangesNotCanceled'));
    },
  });
};

type DeleteJourneyProps = {
  id: string;
  onSuccess?: () => void;
};
export function useDeleteJourney(props: DeleteJourneyProps) {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneys } = useInvalidateJourneys();
  const { invalidateFavorites } = useInvalidateFavorites();

  return useMutation(() => deleteJourney(props.id), {
    onSuccess: () => {
      showSuccess(t('journeyDeleted'));
      waitForElasticRefresh(() => invalidateJourneys());
      props.onSuccess?.();
      invalidateFavorites();
    },
    onError: () => {
      handleError(t('journeyNotDeleted'));
    },
  });
}

export const useUpdateJourneyUserSettings = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: JourneyUserSettings) => {
      return updateJourneyUserSettings(id, settings);
    },
    onMutate: async (newSettings: JourneyUserSettings) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['journey-user-settings', id] });

      const previousSettings = queryClient.getQueryData<JourneyUserSettings['settings']>([
        'journey-user-settings',
        id,
      ]);

      queryClient.setQueryData(['journey-user-settings', id], newSettings.settings);

      return { previousSettings };
    },

    onError: (error, newSettings, context) => {
      if (context?.previousSettings) {
        // If the mutation fails, rollback
        queryClient.setQueryData(['journey-user-settings', id], context.previousSettings);
      }
    },
  });
};

const defaultJourneyUserSettings = {
  viewState: {
    appliedFilters: [],
    conjunction: 'and',
    sortingState: [{ desc: false, id: 'name' }],
  },
} satisfies JourneyUserSettings['settings'];

export const useLoadJourneyUserSettings = (
  id: string,
  options?: UseQueryOptions<JourneyUserSettings['settings']>
) => {
  return useQuery({
    queryKey: ['journey-user-settings', id],
    queryFn: () => loadJourneyUserSettings(id),
    select: (settings) => {
      return {
        ...settings,
        viewState: settings?.viewState ?? defaultJourneyUserSettings.viewState,
      };
    },
    ...options,
  });
};

export const useUpdateJourneySettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: JourneySettings) => {
      const { id, ...rest } = data;
      return updateJourneySettings(id, rest);
    },
    onSuccess: (data: JourneySettings) => {
      const { id, ...rest } = data;
      queryClient.setQueryData(['journey-settings', id], rest);
    },
  });
};

export const useDuplicateJourney = (journeyId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneys, invalidateJourneysStats } = useInvalidateJourneys();

  return useMutation((data: DuplicateJourneyData) => duplicateJourney(journeyId, data), {
    onSuccess: (responseData) => {
      const duplicatedJourneyPath = generatePath(routePaths.journeyDetails, {
        workspaceId: responseData.workspace,
        journeyId: responseData.shortId,
        '*': null,
      });

      waitForElasticRefresh(() => invalidateJourneys());
      invalidateJourneysStats();
      showSuccess(responseData.title, {
        subtitle: t('journeyDuplicated'),
        url: duplicatedJourneyPath,
      });
    },
    onError: () => {
      handleError(t('journeyNotDuplicated'));
    },
  });
};

const getJourneyStatsKey = () => ['journey-stats'];

export const useJourneyAttributes = (
  journeyShortId: string,
  params?: Record<string, string | number | boolean>
) => {
  return useQuery({
    staleTime: 1000,
    queryKey: getJourneyAttributesQueryKey(journeyShortId, params),
    queryFn: () => loadJourneyAttributes(journeyShortId, params),
    enabled: Boolean(journeyShortId),
  });
};

export const useJourneyIssues = (journeyShortId: string) => {
  return useQuery({
    queryKey: getJourneyIssuesQueryKey(journeyShortId),
    queryFn: () => loadJourneyIssues(journeyShortId),
    enabled: Boolean(journeyShortId),
  });
};

export const useUpdateUserCollapse = (journeyShortId: JourneyType['shortId']) => {
  return useMutation({
    mutationFn: (collapsedActions: Array<JourneyAction['shortId']>) =>
      updateJourneyCollapse(journeyShortId, collapsedActions),
  });
};

export const useJourneyOverview = (journeyShortId: string) => {
  return useQuery({
    queryKey: ['journey-overview', journeyShortId],
    queryFn: () => loadJourneyOverview(journeyShortId),
    enabled: Boolean(journeyShortId),
  });
};

export const locale = {
  en: {
    journeyDeleted: 'Journey deleted',
    journeyNotDeleted: 'Journey could not be deleted',
    journeyDuplicated: 'Journey duplicated',
    journeyNotDuplicated: 'Unable to duplicate journey',
    journeyNotPublished: 'Unable to publish journey',
    journeyMoved: 'Journey moved',
    journeyNotMoved: 'Unable to move journey',
    journeyChangesNotCanceled: 'Unable to cancel changes',
    attributeNotSaved: 'Attribute could not be saved',
    attributeOrdersNotSaved: 'Attributes could not be reordered',
  },
};
